import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import JsonLd from "@/components/site/JsonLd";
import { Reveal } from "@/components/site/Reveal";
import { graph, faqPageNode, breadcrumbNode } from "@/lib/schema";
import { useSeo } from "@/lib/seo";
import { useLang } from "@/lib/i18n";
import { LT } from "@/lib/localize";
import api from "@/lib/api";

const PAGE_KEYS = ["title", "intro", "seo_title", "seo_description"];

export default function Faq() {
  const { t, lang } = useLang();
  const [raw, setRaw] = useState(null);

  useEffect(() => {
    api.get("/pages/faq").then((r) => setRaw(r.data)).catch(() => {});
  }, []);

  const page = raw ? LT(raw, lang, PAGE_KEYS) : null;
  const faqs = raw ? raw.faqs.map((f) => LT(f, lang, ["question", "answer"])) : [];
  const title = (page && page.seo_title) || t("seo.faq.title");
  const description = (page && page.seo_description) || t("seo.faq.desc");

  useSeo({ title, description, path: "/faq" });

  const jsonLd = graph([
    faqPageNode({ faqs, title: (page && page.title) || t("nav.faq"), lang }),
    breadcrumbNode([{ name: "Home", path: "/" }, { name: t("nav.faq"), path: "/faq" }], lang),
  ]);

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />
      <JsonLd id="jsonld-faq" data={jsonLd} />

      <section className="pt-32 sm:pt-44 pb-12" data-testid="faq-page">
        <div className="container-x max-w-3xl">
          <Reveal>
            <p className="eyebrow mb-3">{t("faq.eyebrow")}</p>
            <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[0.98]" data-testid="faq-title">
              {(page && page.title) || t("nav.faq")}
            </h1>
            {page && page.intro && <p className="mt-7 text-base text-cream2 leading-relaxed" data-testid="faq-intro">{page.intro}</p>}
          </Reveal>
        </div>
      </section>

      <section className="pb-20" data-testid="faq-list">
        <div className="container-x max-w-3xl">
          {faqs.map((f, i) => (
            <Reveal key={f.id} delay={0.03}>
              <details className="group border-t border-white/8 py-6" data-testid={`faq-item-${i + 1}`}>
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none marker:hidden">
                  <h2 className="font-serif text-xl sm:text-2xl leading-snug group-hover:text-copperlight transition-colors">{f.question}</h2>
                  <Plus className="h-5 w-5 shrink-0 text-copper mt-1 transition-transform duration-300 group-open:rotate-45" />
                </summary>
                <p className="mt-4 text-sm sm:text-base text-cream2 leading-relaxed max-w-2xl" data-testid={`faq-answer-${i + 1}`}>
                  {f.answer}
                </p>
              </details>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="container-x max-w-3xl border border-copper/25 p-8 sm:p-10">
          <p className="eyebrow mb-3">{t("faq.stillEyebrow")}</p>
          <p className="font-serif text-2xl sm:text-3xl leading-tight">{t("faq.stillTitle")}</p>
          <p className="mt-4 text-sm text-cream2 leading-relaxed max-w-xl">{t("faq.stillText")}</p>
          <Link to="/contatti" className="btn-copper mt-7 inline-flex" data-testid="faq-contact-cta">
            {t("faq.stillCta")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
